import React from 'react';

// Conversion-critical modals are imported STATICALLY (not lazy) so they are
// always resolved in memory and render synchronously — no React.lazy/Suspense
// one-frame blank flash, no dependence on a preload finishing first. The
// bundle-size cost is intentional for these high-intent flows.
import LoginModalComponent from '@/components/Modal/LoginModal';
import SignupModalComponent from '@/components/Modal/SignupModal';
import PricingModalComponent from '@/components/Modal/PricingModal';
import CreditsPurchaseModalComponent from '@/components/Modal/CreditsPurchaseModal';
import ConfirmationModalComponent from '@/components/Modal/ConfirmationModal';

/**
 * Eager (statically-bundled) modal. Exposes the same interface as a lazy modal
 * so ModalRoot can treat them uniformly, but every accessor resolves instantly.
 */
function createEagerModal(Component) {
  const resolved = Promise.resolve({ default: Component });
  return {
    isEager: true,
    getComponent: () => Component,
    getModalOptions: () => Component.modalOptions ?? {},
    preload: () => resolved,
  };
}

/**
 * One dynamic import per modal — Metro emits a separate async chunk per entry.
 * modalOptions are read from the loaded module (cached after first open).
 */
function createLazyModal(importFn) {
  let cachedOptions = null;
  let cachedComponent = null;
  let loadPromise = null;

  const ensureLoaded = () => {
    if (!loadPromise) {
      loadPromise = importFn().then((mod) => {
        cachedOptions = mod.default?.modalOptions ?? {};
        cachedComponent = mod.default ?? null;
        return mod;
      });
    }
    return loadPromise;
  };

  const Lazy = React.lazy(ensureLoaded);
  Lazy.preload = ensureLoaded;
  // Prefer live component.modalOptions so HMR/size tweaks aren't stuck on the
  // snapshot taken at first load.
  Lazy.getModalOptions = () => cachedComponent?.modalOptions ?? cachedOptions;
  // Once loaded, callers can render this resolved component DIRECTLY (no
  // React.lazy/Suspense), which avoids the one-frame suspend that React.lazy
  // always incurs on first render — the cause of the brief blank/white modal.
  Lazy.getComponent = () => cachedComponent;
  return Lazy;
}

export const MODAL_COMPONENTS = {
  AuthCTAModal: createLazyModal(() => import('@/components/Modal/AuthCTAModal')),
  GhostSignupModal: createLazyModal(() => import('@/components/Modal/GhostSignupModal')),
  LoginModal: createEagerModal(LoginModalComponent),
  PricingModal: createEagerModal(PricingModalComponent),
  CreditsPurchaseModal: createEagerModal(CreditsPurchaseModalComponent),
  GiveBotJobModal: createLazyModal(() => import('@/components/Modal/GiveBotJobModal')),
  BotPickerModal: createLazyModal(() => import('@/components/Modal/BotPickerModal')),
  WorkSessionLimitModal: createLazyModal(() => import('@/components/Modal/WorkSessionLimitModal')),
  SettingsModalWeb: createLazyModal(() => import('@/components/Modal/SettingsModal_web')),
  SettingsModalMobile: createLazyModal(() => import('@/components/Modal/SettingsModal_mobile')),
  AgentDetailsModal: createLazyModal(() => import('@/components/Modal/AgentDetailsModal')),
  CancelPurchaseModal: createLazyModal(() => import('@/components/Modal/CancelPurchaseModal')),
  CancellationSaveModal: createLazyModal(() => import('@/components/Modal/CancellationSaveModal')),
  SignupModal: createEagerModal(SignupModalComponent),
  InputModal: createLazyModal(() => import('@/components/Modal/InputModal')),
  CustomAgentModal: createLazyModal(() => import('@/components/Modal/CustomAgentModal')),
  ShareModal: createLazyModal(() => import('@/components/Modal/ShareModal')),
  CollectionCustomInstructionsModal: createLazyModal(() => import('@/components/Modal/CollectionCustomInstructionsModal')),
  CollectionMemorySettingsModal: createLazyModal(() => import('@/components/Modal/CollectionMemorySettingsModal')),
  CollectionSettingsModal: createLazyModal(() => import('@/components/Modal/CollectionSettingsModal')),
  CollectionFilesModal: createLazyModal(() => import('@/components/Modal/CollectionFilesModal')),
  FileUploadModal: createLazyModal(() => import('@/components/Modal/FileUploadModal')),
  FileEditModal: createLazyModal(() => import('@/components/Modal/FileEditModal')),
  FilesModal: createLazyModal(() => import('@/components/Modal/FilesModal')),
  FeedbackModal: createLazyModal(() => import('@/components/Modal/FeedbackModal')),
  ReportModal: createLazyModal(() => import('@/components/Modal/ReportModal')),
  // Archive/delete/confirm is used from chat history, so keep this in the
  // main bundle. A lazy chunk here is what surfaced Metro's
  // "Got unexpected undefined" overlay when archiving from Your Chats.
  ConfirmationModal: createEagerModal(ConfirmationModalComponent),
  ChatDetailsModal: createLazyModal(() => import('@/components/Modal/ChatDetailsModal')),
  ModelDetailsModal: createLazyModal(() => import('@/components/Modal/ModelDetailsModal')),
  CreateCollectionModal: createLazyModal(() => import('@/components/Modal/CreateCollectionModal')),
  CreateMemoryModal: createLazyModal(() => import('@/components/Modal/CreateMemoryModal')),
  CreditsRequiredModal: createLazyModal(() => import('@/components/Modal/CreditsRequiredModal')),
  ModelSelectorDetailedModal: createLazyModal(() => import('@/components/Modal/ModelSelectorDetailedModal')),
  ChatInputCustomParametersModal: createLazyModal(() => import('@/components/Modal/ChatInputCustomParametersModal')),
  ComposerToolsMenu: createLazyModal(() => import('@/components/ChatInput/ComposerToolsMenu')),
  CoworkerComingSoonModal: createLazyModal(() => import('@/components/Modal/CoworkerComingSoonModal')),
  CollectionsIconGridModal: createLazyModal(() => import('@/components/Modal/CollectionsIconGridModal')),
  CreateTaskModal: createLazyModal(() => import('@/components/Modal/CreateTaskModal')),
  WorkChatTasksModal: createLazyModal(() => import('@/components/Modal/WorkChatTasksModal')),
  MusicLyricsModal: createLazyModal(() => import('@/components/Modal/MusicLyricsModal')),
  MusicStylePickerModal: createLazyModal(() => import('@/components/Modal/MusicStylePickerModal')),
  ImagePromptModal: createLazyModal(() => import('@/components/Modal/ImagePromptModal')),
  VideoPromptModal: createLazyModal(() => import('@/components/Modal/VideoPromptModal')),
  ThreeDPromptModal: createLazyModal(() => import('@/components/Modal/ThreeDPromptModal')),
  WebSearchModal: createLazyModal(() => import('@/components/Modal/WebSearchModal')),
  MemoryInfoModal: createLazyModal(() => import('@/components/Modal/MemoryInfoModal')),
  TemporaryChatSettingsModal: createLazyModal(() => import('@/components/Modal/TemporaryChatSettingsModal')),
  TemporaryChatInfoModal: createLazyModal(() => import('@/components/Modal/TemporaryChatInfoModal')),
  VoiceCreditsDepletedModal: createLazyModal(() => import('@/components/Modal/VoiceCreditsDepletedModal')),
  VoiceMediaErrorModal: createLazyModal(() => import('@/components/Modal/VoiceMediaErrorModal')),
  VoiceVisionPermissionModal: createLazyModal(() => import('@/components/Modal/VoiceVisionPermissionModal')),
  CreditsInfoModal: createLazyModal(() => import('@/components/Modal/CreditsInfoModal')),
  SaveImageInstructionsModal: createLazyModal(() => import('@/components/Modal/SaveImageInstructionsModal')),
  OutOfCreditsModal: createLazyModal(() => import('@/components/Modal/OutOfCreditsModal')),
  ModelAccessPaywallModal: createLazyModal(() => import('@/components/Modal/ModelAccessPaywallModal')),
  OnboardingCompleteModal: createLazyModal(() => import('@/components/Modal/OnboardingCompleteModal')),
  CreateAPIKeyModal: createLazyModal(() => import('@/components/Modal/CreateAPIKeyModal')),
  QuickPurchaseConfirmModal: createLazyModal(() => import('@/components/Modal/QuickPurchaseConfirmModal')),
  AutoTopUpPromptModal: createLazyModal(() => import('@/components/Modal/AutoTopUpPromptModal')),
  AutoTopUpConfigModal: createLazyModal(() => import('@/components/Modal/AutoTopUpConfigModal')),
  InstallAppModal: createLazyModal(() => import('@/components/Modal/InstallAppModal')),
  DownloadAppsModal: createLazyModal(() => import('@/components/Modal/DownloadAppsModal')),
  MoveToCollectionModal: createLazyModal(() => import('@/components/Modal/MoveToCollectionModal')),
  MoveChatsToCollectionModal: createLazyModal(() => import('@/components/Modal/MoveChatsToCollectionModal')),
  AddFilesToCollectionModal: createLazyModal(() => import('@/components/Modal/AddFilesToCollectionModal')),
  SetupTaskFromTemplateModal: createLazyModal(() => import('@/components/Modal/SetupTaskFromTemplateModal')),
  MemoryBuilderModal: createLazyModal(() => import('@/components/Modal/MemoryBuilderModal')),
  ImportMemoryModal: createLazyModal(() => import('@/components/Modal/ImportMemoryModal')),
  ConversationSelectModal: createLazyModal(() => import('@/components/Modal/ConversationSelectModal')),
  SubscriptionExtendModal: createLazyModal(() => import('@/components/Modal/SubscriptionExtendModal')),
  SubscriptionCheckoutModal: createLazyModal(() => import('@/components/Modal/SubscriptionCheckoutModal')),
  CancelSubscriptionGuideModal: createLazyModal(() => import('@/components/Modal/CancelSubscriptionGuideModal')),
  EditMessageModal: createLazyModal(() => import('@/components/Modal/EditMessageModal')),
  MessageSpeakModal: createLazyModal(() => import('@/components/Modal/MessageSpeakModal')),
  SearchModal: createLazyModal(() => import('@/components/Modal/SearchModal')),
  BookmarkFolderPickerModal: createLazyModal(() => import('@/components/Modal/BookmarkFolderPickerModal')),
  BookmarkFolderFormModal: createLazyModal(() => import('@/components/Modal/BookmarkFolderFormModal')),
  MoveCollectionModal: createLazyModal(() => import('@/components/Modal/MoveCollectionModal')),
  ToolPickerModal: createLazyModal(() => import('@/components/Modal/ToolPickerModal')),
  AccountReviewHoldModal: createLazyModal(() => import('@/components/Modal/AccountReviewHoldModal')),
  VerifyEmailModal: createLazyModal(() => import('@/components/Modal/VerifyEmailModal')),
  VerifyPhoneModal: createLazyModal(() => import('@/components/Modal/VerifyPhoneModal')),
  PetPickerModal: createLazyModal(() => import('@/components/Modal/PetPickerModal')),
};

/**
 * Conversion-critical modals are now statically bundled (createEagerModal), so
 * there's no async chunk to preload. List any remaining LAZY modals here that
 * should still be warmed up after mount.
 */
export const CRITICAL_PRELOAD_MODALS = [];

export function preloadModal(modalName) {
  const component = MODAL_COMPONENTS[modalName];
  if (!component?.preload) return Promise.resolve();
  return component.preload();
}

let criticalPreloadPromise = null;

export function preloadCriticalModals() {
  if (!criticalPreloadPromise) {
    criticalPreloadPromise = Promise.all(
      CRITICAL_PRELOAD_MODALS.map((name) => preloadModal(name)),
    ).catch((err) => {
      criticalPreloadPromise = null;
      throw err;
    });
  }
  return criticalPreloadPromise;
}

export function isRegisteredModal(modalName) {
  return Boolean(modalName && MODAL_COMPONENTS[modalName]);
}
